import React from 'react';
import { useParams } from 'react-router-dom';

import { useMediaQuery } from '@mui/material';
import { FaCheck, FaTimes } from 'react-icons/fa';

import { OButton, ODropdownSplitButton } from '@src/shared';
import { EUserType, TWebinarInviteesType } from '@src/types';
import { IAttendee } from '@src/webinar/types';

import InviteModal from '../InviteModal';
import WebinarModal from '../WebinarModal';

import { useWebinarActionCell } from './useWebinarActionCell';

import { STATUS_LABELS, STATUS_VARIANTS } from '@src/constants';

import styles from './webinarActionCell.module.scss';

interface WebinarActionCellProps {
  attendee: IAttendee;
}

const WebinarActionCell = ({ attendee }: WebinarActionCellProps) => {
  const { id: roomId } = useParams();
  const {
    currentStatus,
    loadingStatus,
    inviteRole,
    modalConfig,
    setModalConfig,
    setInviteRole,
    handleStatusChange,
    dropdownItems,
    sendRoleChangeNotification,
    isSendingInvitation,
    fmtRole,
    isWebinarActionPending,
    isActionDisabled,
    actionTooltip,
    isMainButtonDisabled,
    attendeesIdList,
    setAttendeesIdList,
  } = useWebinarActionCell(attendee, roomId);

  const isMobile = useMediaQuery('(max-width:768px)');
  const isPending = currentStatus === 'PENDING';
  return (
    <div className={styles.outerContainer}>
      <div className={styles.innerContainer}>
        {currentStatus === 'PENDING' && (
          <OButton
            text={isMobile && isPending ? <FaTimes stroke="1px" /> : 'Reject'}
            variant="danger"
            size="default"
            outlined
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange('REJECTED', 'first');
            }}
            loading={isWebinarActionPending && loadingStatus === 'first'}
            classes={{ container: styles.rejectButton }}
          />
        )}

        <ODropdownSplitButton
          text={isMobile && isPending ? <FaCheck /> : STATUS_LABELS[currentStatus]}
          variant={STATUS_VARIANTS[currentStatus]}
          dropdownModel={dropdownItems}
          onClick={
            currentStatus === 'PENDING'
              ? () => {
                  handleStatusChange('APPROVED');
                }
              : undefined
          }
          loading={isWebinarActionPending && loadingStatus !== 'first'}
          isSplitTransparent
          disabled={attendeesIdList.has(attendee._id) || isActionDisabled}
          tooltip={actionTooltip}
          disableMainButton={isMainButtonDisabled}
        />

        {modalConfig && (
          <WebinarModal
            isOpen
            onClose={() => setModalConfig(null)}
            title={modalConfig.title}
            description={modalConfig.description}
            actions={[
              {
                text: 'Cancel',
                variant: 'primary',
                outlined: true,
                size: 'default',
                onClick: () => setModalConfig(null),
              },
              {
                text: 'Confirm',
                variant: 'success',
                loading: isSendingInvitation,
                size: 'default',
                onClick: () => {
                  sendRoleChangeNotification(modalConfig.role);
                },
              },
            ]}
          />
        )}

        {inviteRole && (
          <InviteModal
            setAttendeesIdList={setAttendeesIdList}
            attendee={attendee}
            open={Boolean(inviteRole)}
            onClose={() => setInviteRole(null)}
            userRole={inviteRole as TWebinarInviteesType}
            email={attendee.email}
            title={
              attendee.type === EUserType.ATTENDEE
                ? `Promote attendee to`
                : `Change ${fmtRole(attendee.type).replace('_', '-')} to ${fmtRole(inviteRole || '').replace('_', '-')}`
            }
            isRadioFieldsVisible={attendee.type === EUserType.ATTENDEE}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(WebinarActionCell);
